import React, { useState, useEffect } from 'react';
import { X, Search, Check, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as walletService from '../services/walletService';

const ReconciliationModal = ({ isOpen, onClose, order, onSuccess }) => {
    const [pendingMovements, setPendingMovements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [linking, setLinking] = useState(false);

    useEffect(() => {
        if (isOpen && order) {
            fetchMovements();
        }
    }, [isOpen, order]);

    const fetchMovements = async () => {
        try {
            setLoading(true);
            const response = await walletService.getBancolombiaMovements(500, 0, { status: 'pending' });
            setPendingMovements(response.data || []);
        } catch (error) {
            console.error('Error fetching movements:', error);
            toast.error('Error cargando movimientos');
        } finally {
            setLoading(false);
        }
    };

    const handleLinkMovement = async (movement) => {
        try {
            setLinking(true);
            await walletService.linkMovementToOrder(order.id, movement.id);
            toast.success('✅ Movimiento vinculado exitosamente');
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('Error linking movement:', error);
            toast.error('Error vinculando movimiento');
        } finally {
            setLinking(false);
        }
    };

    const filteredMovements = pendingMovements.filter(m => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            m.valor.toString().includes(search) ||
            (m.descripcion || '').toLowerCase().includes(search) ||
            (m.referencia || '').toLowerCase().includes(search)
        );
    });

    if (!isOpen || !order) return null;

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('es-CO').format(Math.round(amount));
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-CO', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Conciliar Pedido</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            <span className="font-bold">{order.order_number}</span> - ${formatAmount(order.total_amount)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-6 border-b">
                    <div className="relative">
                        <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Buscar por monto, descripción o referencia..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>

                {/* Movements List */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                        </div>
                    ) : filteredMovements.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <p>No hay movimientos pendientes disponibles</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredMovements.map((movement) => (
                                <button
                                    key={movement.id}
                                    onClick={() => handleLinkMovement(movement)}
                                    disabled={linking}
                                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="font-bold text-lg text-gray-900">
                                                ${formatAmount(movement.valor)}
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1">
                                                {movement.descripcion}
                                            </div>
                                            {movement.referencia && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Ref: {movement.referencia}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-right ml-4">
                                            <div className="text-sm text-gray-600">
                                                {formatDate(movement.fecha)}
                                            </div>
                                            {Math.abs(movement.valor - order.total_amount) < 100 && (
                                                <div className="mt-1">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <Check className="w-3 h-3 mr-1" />
                                                        Coincide
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReconciliationModal;
